import React from 'react';
import { fs, makeFields, FarAiAssist, Getter, Setter } from './farFields';

/* FAR Part 15 — Contracting by Negotiation (12 sections).
 * Shown when the CO-selected method is Part 15. Proposals, evaluations,
 * discussions, tradeoffs, and negotiations. */

export default function Far15Form({ get, set }: { get: Getter; set: Setter }) {
  const F = makeFields(get, set);
  const discussions = get('will_discussions') === 'Yes';
  const bestValue = (get('source_selection_method') || '').toLowerCase().includes('tradeoff');

  return (
    <div>
      <div style={{ padding: '10px 14px', background: '#faf5ff', border: '1px solid #e9d5ff', borderRadius: 8, marginBottom: 16 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: '#6b21a8' }}>FAR Part 15 — Contracting by Negotiation</span>
        <p style={{ fontSize: 11, color: '#7e22ce', margin: '2px 0 0' }}>Proposals, evaluations, discussions, tradeoffs, and negotiations. Each offeror's proposal and discussion information must be kept separate.</p>
      </div>
      <FarAiAssist formName="FAR Part 15 — Contracting by Negotiation" get={get} set={set}
        fields={[
          { key: 'why_negotiated', label: 'Why Negotiated Procedures Are Appropriate', kind: 'long' },
          { key: 'rfp_price_nonprice', label: 'Relationship Between Price and Non-Price Factors', kind: 'long' },
          { key: 'rfp_past_performance', label: 'Past Performance Approach', kind: 'long' },
          { key: 'final_tradeoff', label: 'Tradeoff Analysis', kind: 'long' },
        ]} />

      {/* Section 1 — Negotiated-Acquisition Strategy */}
      <div style={fs.section}>
        <h3 style={fs.title}>Section 1 · Negotiated-Acquisition Strategy</h3>
        {F.Sel('acquisition_type', 'Acquisition Type', ['Competitive', 'Sole Source'])}
        {F.Long('why_negotiated', 'Why Negotiated Procedures Are Appropriate')}
        {F.Sel('source_selection_method', 'Source-Selection Method', ['Lowest price technically acceptable', 'Best-value tradeoff', 'Highest technically rated with fair and reasonable price (when authorized)', 'Other approved method'])}
        {F.YN('oral_presentations', 'Oral Presentations Permitted?')}
        {F.YN('advisory_multistep', 'Advisory Multi-Step Process Used?')}
        {F.YN('will_discussions', 'Will Discussions Be Conducted?', ['Yes', 'No', 'To Be Determined'])}
        {F.Text('ss_plan', 'Source-Selection Plan (reference)')}
        {F.Text('ss_authority', 'Source-Selection Authority')}
        {F.TableField('eval_team', 'Evaluation Team Members and Roles', [
          { key: 'name', label: 'Member', type: 'text' },
          { key: 'role', label: 'Role', type: 'text' },
          { key: 'coi', label: 'COI cleared?', type: 'select', options: ['Yes', 'No', 'Pending'] },
        ])}
      </div>

      {/* Section 2 — Pre-Solicitation Exchanges */}
      <div style={fs.section}>
        <h3 style={fs.title}>Section 2 · Pre-Solicitation Exchanges</h3>
        {F.MultiSelect('pre_activities', 'Industry Engagement Activities', ['Sources sought', 'Request for information', 'Draft RFP', 'Industry day', 'One-on-one meetings', 'Pre-solicitation conference', 'Other'])}
        {F.TableField('pre_qa', 'Industry Questions and Government Answers', [
          { key: 'question', label: 'Question', type: 'long' },
          { key: 'answer', label: 'Government answer', type: 'long' },
        ])}
        {F.YN('pre_oci_review', 'Organizational Conflict-of-Interest Review Completed?')}
        {F.YN('pre_integrity_controls', 'Procurement Integrity Controls Established?')}
        {F.Long('pre_requirement_changes', 'Requirement Changes Resulting From Engagement')}
        {F.Text('pre_record', 'Industry Engagement Record (reference)')}
      </div>

      {/* Section 3 — RFP Development */}
      <div style={fs.section}>
        <h3 style={fs.title}>Section 3 · RFP Development</h3>
        {F.Text('rfp_number', 'RFP Number')}
        {F.Text('rfp_due', 'Proposal Due Date and Time')}
        {F.YN('rfp_ucf', 'Uniform Contract Format Used?', ['Yes', 'No', 'Not Applicable'])}
        <div style={fs.field}>
          <label style={fs.label}>RFP Sections Complete?</label>
          {F.Checklist('rfp_sections', ['Part I — Schedule', 'Part II — Contract clauses', 'Part III — Documents, exhibits, and attachments', 'Part IV — Representations and instructions'])}
        </div>
        {F.Long('rfp_instructions', 'Instructions to Offerors')}
        {F.Text('rfp_page_limits', 'Proposal Page and Format Limits')}
        {F.TableField('rfp_eval_factors', 'Evaluation Factors and Significant Subfactors', [
          { key: 'factor', label: 'Factor', type: 'text' },
          { key: 'subfactor', label: 'Subfactor', type: 'text' },
          { key: 'rating_method', label: 'Rating method', type: 'text' },
          { key: 'importance', label: 'Relative importance', type: 'text' },
          { key: 'evidence', label: 'Evidence required', type: 'text' },
        ])}
        {F.Long('rfp_price_nonprice', 'Relationship Between Price and Non-Price Factors', { req: true })}
        {F.Long('rfp_past_performance', 'Past Performance Approach')}
        {F.YN('rfp_small_business', 'Small-Business Participation Evaluated?')}
        {F.Sel('rfp_status', 'RFP Review and Approval Status', ['Draft', 'Legal Review', 'Approved', 'Issued'])}
      </div>

      {/* Section 4 — RFP Issuance and Amendments */}
      <div style={fs.section}>
        <h3 style={fs.title}>Section 4 · RFP Issuance and Amendments</h3>
        {F.Date_('iss_date', 'RFP Issued Date')}
        {F.Text('iss_public_notice', 'Public Notice Reference')}
        {F.Text('iss_questions_deadline', 'Questions Deadline')}
        {F.TableField('iss_qa', 'Government Q&A Register', [
          { key: 'question', label: 'Question', type: 'long' },
          { key: 'answer', label: 'Answer', type: 'long' },
        ])}
        {F.TableField('iss_amendments', 'Amendments', [
          { key: 'amendment', label: 'Amendment', type: 'text' },
          { key: 'description', label: 'Description', type: 'long' },
          { key: 'issue_date', label: 'Issue date', type: 'text' },
          { key: 'impact', label: 'Proposal impact', type: 'text' },
          { key: 'acknowledged', label: 'Acknowledged', type: 'select', options: ['Yes', 'No'] },
        ])}
        {F.YN('iss_extended', 'Closing Date Extended?')}
        {get('iss_extended') === 'Yes' && F.Long('iss_extension_rationale', 'Extension Rationale')}
      </div>

      {/* Section 5 — Proposal Receipt and Compliance */}
      <div style={fs.section}>
        <h3 style={fs.title}>Section 5 · Proposal Receipt and Compliance</h3>
        {F.TableField('receipt_register', 'Secure Proposal Register', [
          { key: 'offeror', label: 'Offeror', type: 'text' },
          { key: 'receipt', label: 'Receipt date/time', type: 'text' },
          { key: 'on_time', label: 'On time?', type: 'select', options: ['Yes', 'No'] },
          { key: 'ack', label: 'Amendment acknowledged?', type: 'select', options: ['Yes', 'No'] },
          { key: 'complete', label: 'Complete?', type: 'select', options: ['Yes', 'No'] },
          { key: 'eligible', label: 'Eligible for evaluation?', type: 'select', options: ['Yes', 'No'] },
        ])}
        {F.YN('receipt_late_review', 'Late Proposal Review Required?')}
        {get('receipt_late_review') === 'Yes' && F.Sel('receipt_late_determination', 'Late Proposal Determination', ['Accept', 'Reject', 'Legal Review'])}
        {F.Long('receipt_compliance', 'Proposal Compliance Findings')}
        {F.YN('receipt_access_restrictions', 'Proposal Access Restrictions Applied?')}
      </div>

      {/* Section 6 — Technical and Past-Performance Evaluation */}
      <div style={fs.section}>
        <h3 style={fs.title}>Section 6 · Technical and Past-Performance Evaluation</h3>
        {F.TableField('tech_eval', 'Evaluation Records (per factor/subfactor)', [
          { key: 'offeror', label: 'Offeror', type: 'text' },
          { key: 'evaluator', label: 'Evaluator', type: 'text' },
          { key: 'factor', label: 'Factor', type: 'text' },
          { key: 'rating', label: 'Rating', type: 'text' },
          { key: 'strengths', label: 'Strengths', type: 'long' },
          { key: 'weaknesses', label: 'Weaknesses', type: 'long' },
          { key: 'sig_weaknesses', label: 'Significant weaknesses', type: 'long' },
          { key: 'deficiencies', label: 'Deficiencies', type: 'long' },
          { key: 'risks', label: 'Risks', type: 'long' },
          { key: 'reference', label: 'Proposal reference', type: 'text' },
          { key: 'confidence', label: 'Evaluator confidence', type: 'text' },
        ])}
        {F.YN('tech_independent', 'Independent Evaluations Completed?')}
        {F.YN('tech_consensus', 'Consensus Evaluation Completed?')}
        {F.Text('tech_pp_relevance', 'Past Performance Relevance (rating)')}
        {F.Text('tech_pp_confidence', 'Past Performance Confidence (rating)')}
        {F.YN('tech_stated_factors', 'Evaluation Limited to Stated Factors?')}
        {F.Text('tech_consensus_report', 'Consensus Report (reference)')}
      </div>

      {/* Section 7 — Cost or Price Evaluation */}
      <div style={fs.section}>
        <h3 style={fs.title}>Section 7 · Cost or Price Evaluation</h3>
        {F.TableField('price_eval', 'Cost/Price Evaluation (per offeror)', [
          { key: 'offeror', label: 'Offeror', type: 'text' },
          { key: 'proposed', label: 'Proposed price', type: 'text' },
          { key: 'evaluated', label: 'Evaluated price', type: 'text' },
          { key: 'options', label: 'Options included', type: 'text' },
          { key: 'adjustments', label: 'Price adjustments', type: 'text' },
          { key: 'reasonableness', label: 'Price reasonableness', type: 'select', options: ['Reasonable', 'Unreasonable', 'Pending'] },
          { key: 'realism_req', label: 'Price realism required?', type: 'select', options: ['Yes', 'No'] },
          { key: 'realism_result', label: 'Price realism result', type: 'text' },
          { key: 'cost_realism_req', label: 'Cost realism required?', type: 'select', options: ['Yes', 'No'] },
          { key: 'cost_realism_adj', label: 'Cost realism adjustment', type: 'text' },
          { key: 'unbalanced', label: 'Unbalanced pricing?', type: 'select', options: ['Yes', 'No'] },
          { key: 'notes', label: 'Analysis notes', type: 'long' },
        ])}
        {F.YN('price_certified_data', 'Certified Cost or Pricing Data Required?')}
        {F.Sel('price_exception', 'Applicable Exception', ['Adequate price competition', 'Prices set by law/regulation', 'Commercial products/services', 'Waiver', 'Not applicable'])}
        {F.YN('price_other_data', 'Data Other Than Certified Cost or Pricing Data Required?')}
        {F.Text('price_igce', 'Independent Government Cost Estimate')}
        {F.Text('price_analysis_report', 'Cost or Price Analysis Report (reference)')}
      </div>

      {/* Section 8 — Competitive Range and Discussions (only if discussions) */}
      {discussions && (
        <div style={fs.section}>
          <h3 style={fs.title}>Section 8 · Competitive Range and Discussions</h3>
          <div style={{ padding: '8px 12px', background: '#faf5ff', border: '1px solid #e9d5ff', borderRadius: 6, fontSize: 11, color: '#6b21a8', marginBottom: 12 }}>
            Each offeror's proposal and discussion information must be kept separate. Discussions must be meaningful and offerors treated fairly.
          </div>
          {F.YN('cr_required', 'Competitive Range Required?')}
          {F.ListField('cr_offerors', 'Offerors Included in Competitive Range')}
          {F.Long('cr_rationale', 'Competitive-Range Rationale')}
          {F.Text('cr_approval', 'Competitive-Range Approval (user + date)')}
          {F.TableField('cr_discussion_items', 'Discussion Items', [
            { key: 'offeror', label: 'Offeror', type: 'text' },
            { key: 'deficiency', label: 'Deficiency', type: 'long' },
            { key: 'sig_weakness', label: 'Significant weakness', type: 'long' },
            { key: 'adverse_pp', label: 'Adverse past performance', type: 'long' },
            { key: 'question', label: 'Question', type: 'long' },
            { key: 'response', label: 'Response', type: 'long' },
          ])}
          {F.YN('cr_meaningful', 'Discussions Were Meaningful?')}
          {F.YN('cr_fair', 'Offerors Treated Fairly?')}
          {F.YN('cr_fpr_requested', 'Final Proposal Revision Requested?')}
          {F.Text('cr_fpr_deadline', 'Common Final Proposal Revision Deadline')}
          {F.Text('cr_fpr_received', 'Final Proposal Revisions Received (reference)')}
        </div>
      )}

      {/* Section 9 — Final Evaluation and Source Selection */}
      <div style={fs.section}>
        <h3 style={fs.title}>Section 9 · Final Evaluation and Source Selection</h3>
        {F.YN('final_tech_complete', 'Final Technical Evaluation Completed?')}
        {F.YN('final_price_complete', 'Final Cost or Price Evaluation Completed?')}
        {F.Long('final_comparative', 'Comparative Assessment')}
        {bestValue && F.Long('final_tradeoff', 'Tradeoff Analysis (required for best-value tradeoff)', { req: true })}
        {bestValue && F.YN('final_premium_justified', 'Benefits Justify Price Premium?', ['Yes', 'No', 'Not Applicable'])}
        {F.Text('final_recommended', 'Recommended Offeror (system recommendation)')}
        {F.Text('final_ssa_decision', 'Source-Selection Authority Decision')}
        {F.YN('final_independent_judgment', 'Independent Judgment Confirmed?')}
        {F.Text('final_ssdd', 'Source-Selection Decision Document (reference)')}
        {F.YN('final_signed', 'Decision Approved and Signed?')}
      </div>

      {/* Section 10 — Negotiation and Award */}
      <div style={fs.section}>
        <h3 style={fs.title}>Section 10 · Negotiation and Award</h3>
        {F.YN('neg_objectives_req', 'Prenegotiation Objectives Required?')}
        {get('neg_objectives_req') === 'Yes' && F.Long('neg_objectives', 'Prenegotiation Objectives')}
        {F.TableField('neg_sessions', 'Negotiation Sessions', [
          { key: 'date', label: 'Date', type: 'text' },
          { key: 'participants', label: 'Participants', type: 'text' },
          { key: 'summary', label: 'Summary', type: 'long' },
        ])}
        {F.Money('neg_final_price', 'Final Negotiated Price')}
        {F.YN('neg_fair_reasonable', 'Price Fair and Reasonable?')}
        {F.YN('neg_memo_complete', 'Negotiation Memorandum Complete?')}
        {F.YN('neg_responsibility', 'Responsibility Determination Complete?')}
        {F.YN('neg_award_approval', 'Award Approval Complete?')}
        {F.Text('neg_award_doc', 'Award Document (reference)')}
      </div>

      {/* Section 11 — Notifications and Debriefings */}
      <div style={fs.section}>
        <h3 style={fs.title}>Section 11 · Notifications and Debriefings</h3>
        {F.YN('notif_preaward_req', 'Preaward Notices Required?')}
        {F.YN('notif_unsuccessful', 'Unsuccessful Offerors Notified?')}
        {F.YN('notif_successful', 'Successful Offeror Notified?')}
        {F.YN('notif_debrief_requested', 'Debriefing Requested?')}
        {F.Sel('notif_debrief_type', 'Debriefing Type', ['Preaward', 'Postaward'])}
        {F.Date_('notif_debrief_date', 'Debriefing Date')}
        {F.YN('notif_debrief_approved', 'Debriefing Materials Approved?')}
        {F.Text('notif_debrief_record', 'Debriefing Record (reference)')}
        {F.YN('notif_protest', 'Protest Received?')}
        {get('notif_protest') === 'Yes' && F.Long('notif_protest_status', 'Protest Status and Response')}
      </div>

      {/* Section 12 — Final Review */}
      <div style={fs.section}>
        <h3 style={fs.title}>Section 12 · FAR Part 15 Final Review</h3>
        {F.Checklist('final_review', [
          'Negotiated procedures are appropriate.',
          'Source-selection method is documented.',
          'Evaluation factors and relative importance were disclosed.',
          'Source-selection team conflicts were reviewed.',
          'Proposals were securely received and controlled.',
          'Evaluations used only the stated factors.',
          'Strengths, weaknesses, deficiencies, and risks are evidence-based.',
          'Cost or price analysis is complete.',
          'Competitive-range decision is documented, if applicable.',
          'Discussions were meaningful, fair, and properly controlled.',
          'Final proposal revisions were evaluated consistently.',
          'Tradeoffs are documented, if applicable.',
          'Source-selection authority exercised independent judgment.',
          'Price is fair and reasonable.',
          'Negotiation record is complete.',
          'Responsibility determination is complete.',
          'Award approvals are complete.',
          'Required notifications and debriefings were completed.',
          'Contract file contains the full decision and audit record.',
        ])}
      </div>
    </div>
  );
}
